import apiClient from './apiService';

export type VulnerabilitySource = 'assessment' | 'audit' | 'monitoring' | 'self_assessment';
export type VulnerabilitySeverity = 'Critical' | 'High' | 'Medium' | 'Low' | 'Informational';
export type VulnerabilityStatus = 'Open' | 'In-Progress' | 'Resolved' | 'Closed';

export const VULNERABILITY_SOURCES: VulnerabilitySource[] = ['assessment', 'audit', 'monitoring', 'self_assessment'];
export const VULNERABILITY_SEVERITIES: VulnerabilitySeverity[] = ['Critical', 'High', 'Medium', 'Low', 'Informational'];
export const VULNERABILITY_STATUSES: VulnerabilityStatus[] = ['Open', 'In-Progress', 'Resolved', 'Closed'];

export interface RiskVulnerability {
  id: number;
  entity_id: number;
  risk_id: number;
  risk_code?: string;
  risk_name?: string;
  source: VulnerabilitySource;
  source_ref_id?: number | null;
  title?: string | null;
  description: string;
  detected_severity: VulnerabilitySeverity;
  status: VulnerabilityStatus;
  detected_on: string;
  owner_user_id?: number | null;
  owner_name?: string | null;
  remediation_notes?: string | null;
  is_active: number;
  created_by?: number | null;
  created_by_name?: string | null;
  created_on?: string;
  modified_by?: number | null;
  modified_on?: string | null;
}

export interface CreateVulnerabilityPayload {
  description: string;
  title?: string;
  source?: VulnerabilitySource;
  source_ref_id?: number | null;
  detected_severity?: VulnerabilitySeverity;
  owner_user_id?: number | null;
}

export interface UpdateVulnerabilityPayload {
  title?: string;
  description?: string;
  detected_severity?: VulnerabilitySeverity;
  status?: VulnerabilityStatus;
  owner_user_id?: number | null;
  remediation_notes?: string;
}

const BASE = '/api/v1/rsk';

export const riskVulnerabilityService = {
  async listForRisk(
    riskId: number,
    opts?: { source?: VulnerabilitySource; status?: VulnerabilityStatus; source_ref_id?: number },
  ): Promise<RiskVulnerability[]> {
    const params: Record<string, string | number> = {};
    if (opts?.source) params.source = opts.source;
    if (opts?.status) params.status = opts.status;
    if (opts?.source_ref_id != null) params.source_ref_id = opts.source_ref_id;
    const resp = await apiClient.get(`${BASE}/risks/${riskId}/vulnerabilities`, { params });
    return resp.data?.data ?? [];
  },

  async listAll(opts?: {
    source?: VulnerabilitySource;
    status?: VulnerabilityStatus;
    severity?: VulnerabilitySeverity;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{ data: RiskVulnerability[]; total: number }> {
    const params: Record<string, string | number> = { limit: opts?.limit ?? 200 };
    if (opts?.source)   params.source   = opts.source;
    if (opts?.status)   params.status   = opts.status;
    if (opts?.severity) params.severity = opts.severity;
    if (opts?.search)   params.search   = opts.search;
    if (opts?.page)     params.page     = opts.page;
    const resp = await apiClient.get(`${BASE}/vulnerabilities`, { params });
    return { data: resp.data?.data ?? [], total: resp.data?.meta?.total ?? 0 };
  },

  async create(riskId: number, payload: CreateVulnerabilityPayload): Promise<RiskVulnerability> {
    const resp = await apiClient.post(`${BASE}/risks/${riskId}/vulnerabilities`, payload);
    return resp.data?.data;
  },

  async update(id: number, payload: UpdateVulnerabilityPayload): Promise<void> {
    await apiClient.put(`${BASE}/vulnerabilities/${id}`, payload);
  },

  async remove(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/vulnerabilities/${id}`);
  },
};

export default riskVulnerabilityService;
